<template>
  <div class="ah-page py-3">
    <header class="ah-toolbar">
      <div class="ah-toolbar__title">
        <span class="ah-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Attendance — Hours</h1>
          <p>Hours each employee spent on-site, from the office biometric device. First punch of the day = clock-in, last = clock-out.</p>
        </div>
      </div>
      <div class="ah-toolbar__actions">
        <button type="button" class="ah-refresh" :class="{ 'is-loading': isLoading }" :disabled="isLoading" @click="loadReport">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ah-refresh__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ah-alert">{{ errorMessage }}</CAlert>

    <section class="ah-section">
      <header class="ah-section__head">
        <h2>Filters</h2>
        <small class="ah-section__hint">{{ rangeLabel }}</small>
      </header>
      <div class="ah-section__body">
        <div class="ah-filters">
          <div class="ah-field ah-field--range">
            <label class="ah-label">Date Range</label>
            <DateRangePicker v-model="dateRange" variant="form" />
          </div>
          <div class="ah-field">
            <label class="ah-label">
              Device / Branch
              <span v-if="isDeviceLocked" class="ah-label__lock">
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" />
                </svg>
                Locked to {{ scopeBranchName }}
              </span>
            </label>
            <AppDropdown
              :value="deviceSn"
              :options="deviceOptions"
              :disabled="isDeviceFilterDisabled"
              @input="onDeviceChange"
            />
          </div>
          <div class="ah-field">
            <label class="ah-label">Status</label>
            <AppDropdown v-model="statusFilter" :options="statusOptions" />
          </div>
          <div class="ah-field ah-field--search">
            <label class="ah-label">Search</label>
            <div class="ah-search">
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ah-search__icon" aria-hidden="true">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input v-model.trim="search" type="text" class="ah-search__input" placeholder="Employee name or code…">
              <button v-if="search" type="button" class="ah-search__clear" aria-label="Clear search" @click="search = ''">×</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <CAlert v-if="unmappedCount" color="warning" show class="ah-unmapped">
      <strong>{{ unmappedCount }}</strong> unmapped {{ unmappedCount === 1 ? 'PIN is' : 'PINs are' }} punching but not linked to a staff member — map them on the user form so their hours are attributed.
    </CAlert>

    <div class="ah-kpis">
      <div v-for="kpi in kpis" :key="kpi.key" class="ah-kpi">
        <span class="ah-kpi__icon" :class="'ah-kpi__icon--' + kpi.tone" aria-hidden="true" v-html="kpi.icon"></span>
        <div class="ah-kpi__meta">
          <span class="ah-kpi__label">{{ kpi.label }}</span>
          <span class="ah-kpi__value">{{ kpi.value }}<small v-if="kpi.unit" class="ah-kpi__unit">{{ kpi.unit }}</small></span>
        </div>
      </div>
    </div>

    <BaseTable
      title="Employees"
      :items="filteredRows"
      :columns="columns"
      :loading="isLoading"
      :paginated="filteredRows.length > pageSize"
      :page-size="pageSize"
      :default-sort="{ column: 'hours', direction: 'desc' }"
      empty-text="No attendance for the selected dates."
    >
      <template #toolbar>
        <span v-if="!isLoading && rows.length" class="ah-table-hint">{{ filteredRows.length }} of {{ rows.length }} shown</span>
      </template>

      <template #cell-name="{ row }">
        <div class="ah-emp">
          <span class="ah-avatar" :class="{ 'ah-avatar--unmapped': row.isUnmapped, 'ah-avatar--photo': photoUrl(row) }">
            <img v-if="photoUrl(row)" :src="photoUrl(row)" :alt="row.name" class="ah-avatar__img" @error="onAvatarError(row)">
            <span v-else>{{ row.initials }}</span>
          </span>
          <div class="ah-emp__text">
            <span class="ah-emp__name">{{ row.name }}</span>
            <span v-if="row.employee_code" class="ah-emp__code">{{ row.employee_code }}</span>
          </div>
        </div>
      </template>

      <template #cell-first_in="{ row }">
        <span v-if="row.first_in" class="ah-time">{{ row.firstInDisplay }}</span>
        <span v-else class="ah-muted">—</span>
      </template>

      <template #cell-last_out="{ row }">
        <span v-if="row.last_out" class="ah-time">{{ row.lastOutDisplay }}</span>
        <span v-else class="ah-muted">—</span>
      </template>

      <template #cell-hours="{ row }">
        <div v-if="row.punches" class="ah-hours">
          <div class="ah-hours__head">
            <strong>{{ row.hoursDisplay }}</strong>
          </div>
          <div class="ah-bar" :title="row.hours + ' h'">
            <span class="ah-bar__fill" :class="'ah-bar__fill--' + row.barTone" :style="{ width: row.barPct + '%' }"></span>
          </div>
        </div>
        <span v-else class="ah-muted">—</span>
      </template>

      <template #cell-punches="{ row }">
        <span class="ah-punches">{{ row.punches }}</span>
      </template>

      <template #cell-status="{ row }">
        <span :class="['ah-pill', 'ah-pill--' + row.status]">
          <span class="ah-pill__dot" aria-hidden="true"></span>{{ statusLabel(row.status) }}
        </span>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import BaseTable from '@/components/BaseTable.vue'

// A nominal full workday — the hours bar fills relative to this, capped at 100%.
const STANDARD_WORKDAY_HOURS = 9

export default {
  name: 'AttendanceHoursReport',
  components: { AppDropdown, DateRangePicker, BaseTable },
  data () {
    const today = this.localToday()
    return {
      fromDate: today,
      toDate: today,
      rows: [],
      isLoading: false,
      errorMessage: '',
      search: '',
      statusFilter: '',
      deviceSn: (this.$route && this.$route.query && this.$route.query.device) || '',
      devices: [],
      // Set when the API confines this user to one branch (e.g. sales_admin) —
      // the device filter then only offers that branch's units.
      deviceScope: null,
      pageSize: 25,
      brokenAvatars: {},
      statusOptions: [
        { value: '', label: 'All Statuses' },
        { value: 'present', label: 'Present' },
        { value: 'incomplete', label: 'One Punch' },
        { value: 'absent', label: 'Absent' }
      ],
      columns: [
        { key: 'name', label: 'Employee' },
        { key: 'date', label: 'Date', format: 'date' },
        { key: 'first_in', label: 'First In' },
        { key: 'last_out', label: 'Last Out' },
        { key: 'hours', label: 'Hours', align: 'right', width: '150px' },
        { key: 'punches', label: 'Punches', align: 'right' },
        { key: 'status', label: 'Status' }
      ]
    }
  },
  computed: {
    dateRange: {
      get () {
        return { fromDate: this.fromDate, toDate: this.toDate }
      },
      set (value) {
        this.fromDate = (value && value.fromDate) || this.fromDate
        this.toDate = (value && value.toDate) || this.toDate
        this.loadReport()
      }
    },
    isSingleDay () {
      return this.fromDate === this.toDate
    },
    rangeLabel () {
      if (this.isSingleDay) return this.display(this.fromDate)
      return `${this.display(this.fromDate)} → ${this.display(this.toDate)}`
    },
    isDeviceLocked () {
      return Boolean(this.deviceScope && this.deviceScope.locked)
    },
    scopeBranchName () {
      return (this.deviceScope && this.deviceScope.branch_name) || 'your branch'
    },
    // A locked user with a single branch device has nothing to choose between.
    isDeviceFilterDisabled () {
      return this.isDeviceLocked && this.devices.length <= 1
    },
    deviceOptions () {
      const options = this.devices.map(d => ({
        value: d.serial,
        label: [d.branch_name, d.label].filter(Boolean).join(' · ') || d.serial
      }))

      if (this.isDeviceLocked) {
        // No "All Devices" escape hatch — the scope is the branch. Only offer
        // the aggregate row when the branch actually has several units.
        return options.length > 1
          ? [{ value: '', label: `All ${this.scopeBranchName} Devices` }].concat(options)
          : options
      }

      return [{ value: '', label: 'All Devices' }].concat(options)
    },
    decoratedRows () {
      const maxHours = Math.max(STANDARD_WORKDAY_HOURS, ...this.rows.map(r => Number(r.hours) || 0))
      return this.rows.map((r) => {
        const hours = Number(r.hours) || 0
        const isUnmapped = /^Unmapped \(PIN/.test(r.name || '')
        return {
          ...r,
          employee_code: r.employee_code || '',
          initials: this.initials(r.name, isUnmapped),
          isUnmapped,
          firstInDisplay: this.timeDisplay(r.first_in),
          lastOutDisplay: this.timeDisplay(r.last_out),
          hoursDisplay: this.hoursDisplay(hours),
          barPct: Math.min(100, Math.round((hours / maxHours) * 100)),
          barTone: this.barTone(hours)
        }
      })
    },
    filteredRows () {
      const q = this.search.toLowerCase()
      return this.decoratedRows.filter((r) => {
        if (this.statusFilter && r.status !== this.statusFilter) return false
        if (!q) return true
        return (r.name || '').toLowerCase().includes(q) ||
          (r.employee_code || '').toLowerCase().includes(q)
      })
    },
    presentCount () {
      return this.rows.filter(r => r.punches > 0).length
    },
    absentCount () {
      return this.rows.filter(r => r.status === 'absent').length
    },
    incompleteCount () {
      return this.rows.filter(r => r.status === 'incomplete').length
    },
    unmappedCount () {
      return this.decoratedRows.filter(r => r.isUnmapped).length
    },
    totalHours () {
      return this.rows.reduce((sum, r) => sum + (Number(r.hours) || 0), 0)
    },
    avgHours () {
      return this.presentCount ? this.totalHours / this.presentCount : 0
    },
    kpis () {
      const present = {
        key: 'present',
        label: this.isSingleDay ? 'Present' : 'Present Days',
        value: this.presentCount,
        tone: 'present',
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>'
      }
      const absent = {
        key: 'absent',
        label: 'Absent',
        value: this.absentCount,
        tone: 'absent',
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
      }
      const incomplete = {
        key: 'incomplete',
        label: 'One Punch',
        value: this.incompleteCount,
        tone: 'incomplete',
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>'
      }
      const totalHours = {
        key: 'total',
        label: 'Total Hours',
        value: this.formatHoursValue(this.totalHours),
        unit: 'h',
        tone: 'brand',
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>'
      }
      const avgHours = {
        key: 'avg',
        label: 'Avg / Present',
        value: this.formatHoursValue(this.avgHours),
        unit: 'h',
        tone: 'brand',
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>'
      }
      // Absent only exists in the single-day view; swap it for One Punch over a range.
      return this.isSingleDay
        ? [present, absent, incomplete, totalHours, avgHours]
        : [present, incomplete, totalHours, avgHours]
    }
  },
  created () {
    this.loadDevices()
    this.loadReport()
  },
  methods: {
    localToday () {
      const d = new Date()
      const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      return local.toISOString().slice(0, 10)
    },
    display (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      return `${d}-${m}-${y}`
    },
    avatarKey (row) {
      return row.user_id != null ? `u${row.user_id}` : `n${row.name}`
    },
    photoUrl (row) {
      if (!row || !row.image || typeof row.image !== 'string') return ''
      if (this.brokenAvatars[this.avatarKey(row)]) return ''
      return row.image
    },
    onAvatarError (row) {
      this.$set(this.brokenAvatars, this.avatarKey(row), true)
    },
    initials (name, isUnmapped) {
      if (isUnmapped) return '?'
      const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '–'
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    },
    timeDisplay (t) {
      if (!t) return '—'
      const [hStr, mStr] = String(t).split(':')
      let h = Number(hStr)
      const m = mStr || '00'
      if (!Number.isFinite(h)) return t
      const suffix = h >= 12 ? 'PM' : 'AM'
      h = h % 12 || 12
      return `${h}:${m} ${suffix}`
    },
    hoursDisplay (hours) {
      const h = Math.floor(hours)
      const m = Math.round((hours - h) * 60)
      if (m === 0) return `${h}h`
      return `${h}h ${String(m).padStart(2, '0')}m`
    },
    formatHoursValue (hours) {
      return (Math.round(hours * 10) / 10).toFixed(1)
    },
    barTone (hours) {
      if (hours >= 8) return 'full'
      if (hours >= 4) return 'mid'
      return 'low'
    },
    statusLabel (s) {
      return { present: 'Present', absent: 'Absent', incomplete: 'One Punch' }[s] || s
    },
    onDeviceChange (val) {
      this.deviceSn = val
      this.loadReport()
    },
    loadDevices () {
      axios.get(`${this.$apiAdress}/api/attendance/devices`)
        .then(r => {
          this.devices = (r.data && r.data.devices) || []
          this.deviceScope = (r.data && r.data.scope) || null
          this.applyDeviceScope()
        })
        .catch(() => { this.devices = [] })
    },
    // Keep the visible filter honest for branch-locked users: a serial from
    // outside the branch (e.g. a stale ?device= query) is dropped, and a
    // single-device branch shows that device rather than a blank select. The
    // API already scopes the data either way, so no reload is needed.
    applyDeviceScope () {
      if (!this.isDeviceLocked) return

      const serials = this.devices.map(d => d.serial)
      if (this.deviceSn && !serials.includes(this.deviceSn)) {
        this.deviceSn = ''
      }
      if (!this.deviceSn && serials.length === 1) {
        this.deviceSn = serials[0]
      }
    },
    loadReport () {
      this.errorMessage = ''
      this.isLoading = true
      axios.get(`${this.$apiAdress}/api/attendance/hours`, {
        params: { from: this.fromDate, to: this.toDate, device_sn: this.deviceSn || undefined }
      })
        .then(r => { this.rows = (r.data && r.data.rows) || [] })
        .catch(e => { this.errorMessage = (e.response && e.response.data && e.response.data.message) || 'Could not load the attendance report.' })
        .finally(() => { this.isLoading = false })
    }
  }
}
</script>

<style scoped>
.ah-page { padding: 0.25rem 0; }

/* ── Toolbar ── */
.ah-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; padding: 0.25rem 0.25rem 1rem; }
.ah-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.ah-toolbar__accent { width: 4px; height: 36px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }
.ah-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.ah-toolbar__title p { margin: 0.12rem 0 0; font-size: 0.78rem; color: #6b7280; max-width: 640px; }
.ah-toolbar__actions { display: flex; align-items: center; gap: 0.5rem; }

.ah-refresh { display: inline-flex; align-items: center; gap: 0.45rem; height: 34px; padding: 0 0.85rem; border-radius: 8px; font-size: 0.82rem; font-weight: 600; background: #fff; border: 1px solid #e5e9f2; color: #1f2a44; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); cursor: pointer; transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease; }
.ah-refresh:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12); }
.ah-refresh:disabled { cursor: progress; background: #f8fafc; color: #4b5563; }
.ah-refresh__icon { flex-shrink: 0; color: #16a07a; transition: transform 0.4s ease; }
.ah-refresh:hover:not(:disabled) .ah-refresh__icon { transform: rotate(60deg); }
.ah-refresh.is-loading .ah-refresh__icon { animation: ah-spin 0.9s linear infinite; }
@keyframes ah-spin { to { transform: rotate(360deg); } }
.ah-refresh:focus { outline: none; }
.ah-refresh::-moz-focus-inner { border: 0; }
.ah-refresh:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

.ah-alert, .ah-unmapped { margin: 0 0 1rem; border-radius: 10px; font-size: 0.85rem; }

/* ── Filter section ── */
.ah-section { background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); margin-bottom: 1.25rem; }
.ah-section__head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; padding: 0.7rem 1rem; border-bottom: 1px solid #eef1f7; background: #fbfcfe; border-radius: 12px 12px 0 0; }
.ah-section__head h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.ah-section__hint { color: #6b7280; font-size: 0.78rem; font-weight: 600; }
.ah-section__body { padding: 1rem; }

.ah-filters { display: flex; flex-wrap: wrap; gap: 0.85rem; align-items: flex-end; }
.ah-field { display: flex; flex-direction: column; flex: 1 1 200px; min-width: 0; }
.ah-field--range { flex: 1 1 320px; }
.ah-field--search { flex: 2 1 280px; }
.ah-label { font-size: 0.78rem; font-weight: 600; color: #4f5560; margin-bottom: 0.3rem; }
.ah-label__lock { display: inline-flex; align-items: center; gap: 0.22rem; margin-left: 0.35rem; padding: 0.05rem 0.4rem; border-radius: 999px; background: #f1f5f9; color: #64748b; font-size: 0.68rem; font-weight: 600; vertical-align: middle; }

.ah-search { position: relative; display: flex; align-items: center; }
.ah-search__icon { position: absolute; left: 0.65rem; color: #94a3b8; pointer-events: none; }
.ah-search__input { width: 100%; height: 36px; padding: 0.3rem 2rem 0.3rem 2.1rem; border: 1px solid #d8dde6; border-radius: 8px; font-size: 0.85rem; color: #2f3640; background: #fff; transition: border-color 0.15s ease, box-shadow 0.15s ease; }
.ah-search__input::placeholder { color: #9ca3af; }
.ah-search__input:focus { outline: none; border-color: #16a07a; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18); }
.ah-search__clear { position: absolute; right: 0.45rem; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 50%; background: #eef1f7; color: #64748b; font-size: 1rem; line-height: 1; cursor: pointer; transition: background 0.15s ease, color 0.15s ease; }
.ah-search__clear:hover { background: #e2e8f0; color: #334155; }
.ah-search__clear:focus { outline: none; }
.ah-search__clear:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }

/* ── KPI cards (white card, brand-green key number) ── */
.ah-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 0.85rem; margin-bottom: 1.25rem; }
.ah-kpi { display: flex; align-items: center; gap: 0.75rem; background: #fff; border: 1px solid #e5e9f2; border-radius: 12px; box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04); padding: 0.85rem 1rem; }
.ah-kpi__icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; }
.ah-kpi__icon--present { background: #dcfce7; color: #16834f; }
.ah-kpi__icon--absent { background: #fee2e2; color: #b91c1c; }
.ah-kpi__icon--incomplete { background: #fef3c7; color: #b45309; }
.ah-kpi__icon--brand { background: #e6f5ef; color: #0e5b46; }
.ah-kpi__meta { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.ah-kpi__label { font-size: 0.72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.ah-kpi__value { font-size: 1.5rem; font-weight: 700; color: #0e5b46; line-height: 1.1; }
.ah-kpi__unit { font-size: 0.8rem; font-weight: 600; color: #16a07a; margin-left: 0.15rem; }

.ah-table-hint { font-size: 0.78rem; color: #6b7280; font-weight: 600; }

/* ── Employee cell ── */
.ah-emp { display: flex; align-items: center; gap: 0.6rem; }
.ah-avatar { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0; overflow: hidden; font-size: 0.72rem; font-weight: 700; color: #0e5b46; background: linear-gradient(135deg, #e6f5ef 0%, #d4ede2 100%); letter-spacing: 0.02em; }
.ah-avatar--unmapped { color: #b45309; background: #fef3c7; }
.ah-avatar--photo { background: #f1f5f9; box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06); }
.ah-avatar__img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ah-emp__text { display: flex; flex-direction: column; min-width: 0; line-height: 1.25; }
.ah-emp__name { font-weight: 600; color: #1f2a44; font-size: 0.86rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ah-emp__code { font-size: 0.72rem; color: #94a3b8; font-variant-numeric: tabular-nums; }

.ah-time { font-variant-numeric: tabular-nums; color: #334155; font-weight: 500; }
.ah-punches { font-variant-numeric: tabular-nums; font-weight: 600; color: #475569; }
.ah-muted { color: #cbd5e1; }

/* ── Hours cell + utilization bar ── */
.ah-hours { display: flex; flex-direction: column; gap: 0.3rem; align-items: flex-end; }
.ah-hours__head strong { font-size: 0.86rem; font-weight: 700; color: #1f2a44; font-variant-numeric: tabular-nums; }
.ah-bar { width: 100%; max-width: 110px; height: 5px; border-radius: 999px; background: #eef1f7; overflow: hidden; }
.ah-bar__fill { display: block; height: 100%; border-radius: 999px; transition: width 0.3s ease; }
.ah-bar__fill--full { background: linear-gradient(90deg, #16a07a, #0e5b46); }
.ah-bar__fill--mid { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
.ah-bar__fill--low { background: linear-gradient(90deg, #f87171, #ef4444); }

/* ── Status pill ── */
.ah-pill { display: inline-flex; align-items: center; gap: 0.35rem; padding: 0.2rem 0.6rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600; white-space: nowrap; }
.ah-pill__dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
.ah-pill--present { background: #dcfce7; color: #166534; }
.ah-pill--absent { background: #fee2e2; color: #991b1b; }
.ah-pill--incomplete { background: #fef3c7; color: #92400e; }

@media (max-width: 640px) {
  .ah-emp__name { max-width: 140px; }
}
</style>
